/**
 * 📈 TrendChart - SVG-viivakaavio aikasarjadatalle
 *
 * Piirtää yhden tai useamman sarjan samaan koordinaatistoon. Ei ulkoisia
 * kaaviokirjastoja - puhdas SVG, joten paketti pysyy kevyenä ja ulkoasu on
 * täysin hallinnassa.
 *
 * Toiminnot:
 * - Hover: pystyviiva (crosshair) + tooltip kaikkien sarjojen arvoista
 * - Automaattinen y-akselin skaalaus ja siistit askelvälit
 * - Legenda (identiteetti ei koskaan pelkän värin varassa)
 */

import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

export interface ChartSeries {
  key: string;
  label: string;
  unit?: string;
  color: string;
  /** Arvot samassa järjestyksessä kuin labels; null = puuttuva mittaus */
  values: (number | null)[];
}

interface TrendChartProps {
  labels: string[];
  series: ChartSeries[];
  height?: number;
}

const PADDING = { top: 16, right: 16, bottom: 34, left: 52 };

/** Siistit y-akselin rajat ja askelväli (1-2-5-10 -sarja). */
function niceScale(min: number, max: number, ticks = 5) {
  if (!isFinite(min) || !isFinite(max)) return { lo: 0, hi: 1, step: 1 };
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    min -= pad;
    max += pad;
  }
  const raw = (max - min) / ticks;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1) * mag;
  return {
    lo: Math.floor(min / step) * step,
    hi: Math.ceil(max / step) * step,
    step,
  };
}

const fmt = (v: number) =>
  Math.abs(v) >= 1000 ? v.toLocaleString('fi-FI', { maximumFractionDigits: 0 })
    : Math.abs(v) >= 10 ? v.toFixed(0)
    : v.toFixed(1);

export const TrendChart: React.FC<TrendChartProps> = ({ labels, series, height = 320 }) => {
  const theme = useTheme();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // Leveys seuraa säiliötä (responsiivisuus ilman kirjastoa)
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const { lo, hi, step } = useMemo(() => {
    const all = series.flatMap((s) => s.values.filter((v): v is number => v != null));
    if (!all.length) return { lo: 0, hi: 1, step: 1 };
    return niceScale(Math.min(...all), Math.max(...all));
  }, [series]);

  const plotW = Math.max(width - PADDING.left - PADDING.right, 10);
  const plotH = Math.max(height - PADDING.top - PADDING.bottom, 10);

  const xOf = useCallback(
    (i: number) => PADDING.left + (labels.length <= 1 ? plotW / 2 : (i / (labels.length - 1)) * plotW),
    [labels.length, plotW]
  );
  const yOf = useCallback(
    (v: number) => PADDING.top + plotH - ((v - lo) / (hi - lo || 1)) * plotH,
    [lo, hi, plotH]
  );

  const yTicks = useMemo(() => {
    const out: number[] = [];
    for (let v = lo; v <= hi + step / 2; v += step) out.push(Number(v.toFixed(6)));
    return out;
  }, [lo, hi, step]);

  // X-akselin nimiöt harvennettuna niin etteivät ne mene päällekkäin
  const xTickIdx = useMemo(() => {
    if (!labels.length) return [];
    const maxTicks = Math.max(2, Math.floor(plotW / 90));
    const stride = Math.max(1, Math.ceil(labels.length / maxTicks));
    const out: number[] = [];
    for (let i = 0; i < labels.length; i += stride) out.push(i);
    if (out[out.length - 1] !== labels.length - 1) out.push(labels.length - 1);
    return out;
  }, [labels, plotW]);

  const pathOf = useCallback(
    (vals: (number | null)[]) => {
      let d = '';
      let pen = false;
      vals.forEach((v, i) => {
        if (v == null) { pen = false; return; }
        d += `${pen ? 'L' : 'M'}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`;
        pen = true;
      });
      return d;
    },
    [xOf, yOf]
  );

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - PADDING.left;
    if (labels.length < 2) return setHoverIdx(labels.length ? 0 : null);
    const i = Math.round((x / plotW) * (labels.length - 1));
    setHoverIdx(i >= 0 && i < labels.length ? i : null);
  };

  const grid = theme.palette.divider;
  const ink = theme.palette.text.secondary;

  if (!labels.length) return null;

  return (
    <Box ref={wrapRef} sx={{ position: 'relative', width: '100%' }}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`Viivakaavio: ${series.map((s) => s.label).join(', ')}`}
        onMouseMove={onMove}
        onMouseLeave={() => setHoverIdx(null)}
        style={{ display: 'block', touchAction: 'none' }}
      >
        {/* Vaakaviivat + y-nimiöt (recessiivinen ruudukko) */}
        {yTicks.map((v) => (
          <g key={v}>
            <line
              x1={PADDING.left} x2={PADDING.left + plotW}
              y1={yOf(v)} y2={yOf(v)}
              stroke={grid} strokeWidth={1}
            />
            <text
              x={PADDING.left - 8} y={yOf(v)}
              textAnchor="end" dominantBaseline="middle"
              fontSize={11} fill={ink}
            >
              {fmt(v)}
            </text>
          </g>
        ))}

        {/* X-nimiöt */}
        {xTickIdx.map((i) => (
          <text
            key={i}
            x={xOf(i)} y={height - 12}
            textAnchor={i === 0 ? 'start' : i === labels.length - 1 ? 'end' : 'middle'}
            fontSize={11} fill={ink}
          >
            {labels[i]}
          </text>
        ))}

        {/* Crosshair */}
        {hoverIdx != null && (
          <line
            x1={xOf(hoverIdx)} x2={xOf(hoverIdx)}
            y1={PADDING.top} y2={PADDING.top + plotH}
            stroke={ink} strokeWidth={1} strokeDasharray="3 3"
          />
        )}

        {/* Sarjat - 2px viivat, ei markereita joka pisteessä */}
        {series.map((s) => (
          <path
            key={s.key}
            d={pathOf(s.values)}
            fill="none"
            stroke={s.color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {/* Hover-pisteet: 2px pintarengas erottaa päällekkäiset merkit */}
        {hoverIdx != null &&
          series.map((s) => {
            const v = s.values[hoverIdx];
            if (v == null) return null;
            return (
              <circle
                key={s.key}
                cx={xOf(hoverIdx)} cy={yOf(v)} r={4}
                fill={s.color}
                stroke={theme.palette.background.paper}
                strokeWidth={2}
              />
            );
          })}
      </svg>

      {/* Tooltip */}
      {hoverIdx != null && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: Math.min(Math.max(xOf(hoverIdx) + 12, 8), Math.max(width - 220, 8)),
            minWidth: 170,
            maxWidth: 260,
            p: 1.25,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 3,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            {labels[hoverIdx]}
          </Typography>
          {series.map((s) => {
            const v = s.values[hoverIdx!];
            return (
              <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.15 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: s.color, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.label}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {v == null ? '–' : `${fmt(v)}${s.unit ? ` ${s.unit}` : ''}`}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default TrendChart;
